<script setup lang="ts">
import type { Except } from 'type-fest'

import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'
import dayjs from 'dayjs'
import { uniqBy } from 'lodash'
import { useRouter } from 'vue-router'

import { useAuthStore } from '@/stores/auth-store'
import useForm from '@/hooks/use-form'
import { DefaultRemark, LoadingList, Port } from '@/types/cargo-loading'
import { countryCodes } from '@/utils/country-codes'
import { DropdownOptionValue, DropdownStringOption } from '@/types/inputs'
import { differenceFromMinutes } from '@/utils/dates'
import { ResourceResponse, uuid } from '@/types/general'
import { emitter } from '@/utils/mitt'
import { fullProviderNumber, nameAndShortCodeString } from '@/utils/cargo-loading'
import { useLazyLoadedList } from '@/hooks/use-lazy-loaded-list'

import MyModal from '@/components/my-components/MyModal.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyInput from '@/components/my-components/form/MyInput.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyTextarea from '@/components/my-components/form/MyTextarea.vue'
import MyWeekDayPicker from '@/components/my-components/form/MyWeekDayPicker.vue'
import MyInputLabel from '@/components/my-components/form/MyInputLabel.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'

interface Form extends Except<
    LoadingList,
    | 'id'
    | 'routes'
    | 'provider'
    | 'companyId'
    | 'providerAccountNumber'
    | 'createdAt'
    | 'updatedAt'
    | 'user'
    | 'userId'
    | 'originPort'
    | 'destinationPorts'
> {
    providerCompanyId: uuid | null
    originPortId: uuid | null
    destinationPortIds: uuid[] | null
}

interface ProviderOption extends DropdownStringOption {
    providerCompanyId: uuid
    providerNumber: string
}

interface Props {
    modelValue: boolean
    id?: string | null
}

const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'close'): void
    (e: 'saved'): void
}>()

const { t } = useI18n()
const authStore = useAuthStore()
const router = useRouter()

const fetching = ref(false)
const selectedWeekday = ref<number>(dayjs().isoWeekday() - 1)
const defaultRemarks = ref<DefaultRemark[]>([])
const selectedRemark = ref<DefaultRemark | null>(null)
const selectedRemarkProvider = ref<DropdownOptionValue | null>(null)
const ports = useLazyLoadedList<DropdownStringOption>(fetchPorts)
const { data, submit, errors, reset, loading } = useForm<Form>({
    providerCompanyId: null,
    providerNumber: '',
    tripNumber: '',
    country: 'DK',
    truckNumber: null,
    trailerNumber: null,
    flightNumber: null,
    remark: '',
    originPortId: null,
    destinationPortIds: [],
    departedAt: dayjs().toISOString(),
    loadedAt: dayjs().toISOString(),
})

const isEditing = computed(() => !!props.id)

const providerOptions = computed<ProviderOption[]>(() => {
    // We'll find the unique providers based on the weekday
    const providers = defaultRemarks.value
        .filter((remark) => remark.weekday === selectedWeekday.value)
        .map((remark) => {
            const key = fullProviderNumber(remark)
            return {
                label: key,
                value: key,
                providerCompanyId: remark.provider.id,
                providerNumber: remark.providerNumber ?? '',
            }
        })

    return uniqBy(providers, 'value')
})

const filteredRemarks = computed(() => {
    const remarkProvider = providerOptions.value.find(
        (option) => option.value === selectedRemarkProvider.value,
    )

    return defaultRemarks.value.filter((remark) => {
        return (
            remark.weekday === selectedWeekday.value &&
            remark.provider.id === remarkProvider?.providerCompanyId &&
            remark.providerNumber === remarkProvider.providerNumber
        )
    })
})

const allCountries = Object.entries(countryCodes).map(([code, data]) => ({
    label: data.name,
    value: code,
}))

const disableTruckFields = computed(
    () => !!data.flightNumber && !data.truckNumber && !data.trailerNumber,
)

const disableFlightField = computed(
    () => (!!data.truckNumber || !!data.trailerNumber) && !data.flightNumber,
)

const noRemarksMessage = computed(() => {
    if (filteredRemarks.value.length > 0) return null

    if (!selectedRemarkProvider.value) {
        return t('pleaseSelectWeekdayAndProvider')
    }

    return t('noDefaultRemarksFound')
})

function closeModal() {
    emit('close')
}

function selectRemark(remark: DefaultRemark | null) {
    if (remark === null) {
        selectedRemark.value = null

        return
    }

    selectedRemark.value = remark

    let now = dayjs()
        .isoWeekday(remark.weekday + 1)
        .startOf('day')
    // We'll always create loading lists for the future.
    // So when it's a tuesday and they select monday we'll use tuesday next week
    if (now.endOf('day').isBefore(dayjs())) {
        now = now.add(1, 'week')
    }
    data.loadedAt = now.add(remark.loadingTime, 'minutes').toISOString()
    data.departedAt = now.add(remark.departureTime, 'minutes').toISOString()
    data.providerCompanyId = remark.provider.id
    data.providerNumber = remark.providerNumber ?? ''
    data.remark = remark.remark ?? ''
}

async function fetchDefaultRemarks() {
    // We'll not fetch if we've already done it
    if (defaultRemarks.value.length > 0) return

    const response = await axios.get<ResourceResponse<DefaultRemark[]>>(
        window.route('cl.company.default-remarks.index', {
            company: authStore.companyId,
            'per-page': 2000,
            filter: { 'loading-list': true },
        }),
    )
    defaultRemarks.value = response.data.data
}

async function fetchPorts(): Promise<DropdownStringOption[]> {
    const companyId: string | null = authStore.companyId

    if (!companyId) return []
    const response = await axios.get<ResourceResponse<Port[]>>(
        window.route('cl.company.ports.index', { company: companyId, 'per-page': 2000 }),
    )

    return response.data.data.map((port) => ({
        value: port.id,
        label: nameAndShortCodeString(port.name, port.shortName),
    }))
}

async function fetchLoadingList() {
    const response = await axios.get<ResourceResponse<LoadingList>>(
        window.route('cl.loading-lists.show', {
            loading_list: props.id,
        }),
    )

    const loadingList = response.data.data

    reset({
        providerCompanyId: loadingList.provider.id,
        providerNumber: loadingList.providerNumber,
        tripNumber: loadingList.tripNumber,
        country: loadingList.country,
        truckNumber: loadingList.truckNumber,
        trailerNumber: loadingList.trailerNumber,
        flightNumber: loadingList.flightNumber,
        remark: loadingList.remark,
        originPortId: loadingList.originPort.id,
        destinationPortIds: loadingList.destinationPorts.map((port) => port.id),
        departedAt: loadingList.departedAt,
        loadedAt: loadingList.loadedAt,
    })
    selectedWeekday.value = dayjs(loadingList.loadedAt).isoWeekday() - 1

    // Set the provider dropdown to the loaded loading list's provider
    selectedRemarkProvider.value = fullProviderNumber(loadingList)

    // We'll match the remark based on the loading list so it can be set as selected
    const matchedRemark = defaultRemarks.value.find((remark) => {
        return (
            remark.weekday === selectedWeekday.value &&
            remark.provider.id === loadingList.provider.id &&
            remark.providerNumber === loadingList.providerNumber &&
            remark.remark === loadingList.remark
        )
    })
    if (matchedRemark) {
        selectedRemark.value = matchedRemark
    }
}

async function onModalOpen() {
    fetching.value = true
    await fetchDefaultRemarks()

    if (props.id) {
        await fetchLoadingList()
        ports.fetch()
    } else {
        reset()
        selectedWeekday.value = dayjs().isoWeekday() - 1
    }

    fetching.value = false
}

async function onSubmit() {
    const route = isEditing.value
        ? window.route('cl.company.loading-lists.update', {
              company: authStore.companyId,
              loading_list: props.id,
          })
        : window.route('cl.company.loading-lists.store', {
              company: authStore.companyId,
          })

    const response = await submit<{ id: uuid }>(isEditing.value ? 'PUT' : 'POST', route)

    if (response) {
        emit('saved')
        closeModal()
        emitter.emit('fetchLoadingLists')

        router.push({
            name: 'cl.loading-lists.show',
            params: { listId: response.id },
            query: { 'is-editing': isEditing.value ? 0 : 1 },
        })
    } else {
        notify({ type: 'error', text: t('saveFailed') })
    }
}

onBeforeMount(() => {
    if (props.modelValue) onModalOpen()
})

watch(
    () => props.modelValue,
    (isModalOpen) => {
        if (isModalOpen) onModalOpen()
    },
)
watch(selectedWeekday, async () => {
    await nextTick()

    // When the new weekday doesn't have a remark with the same provider we'll reset it
    const hasProvider = providerOptions.value.find(
        (option) => option.value === selectedRemarkProvider.value,
    )
    if (!hasProvider) {
        selectedRemarkProvider.value = null
    }
})
</script>

<template>
    <MyModal :value="modelValue" :max-width="1100" @close="closeModal">
        <template #title>
            {{ isEditing ? t('editLoadingList') : t('createLoadingList') }}
        </template>

        <LoaderWrapper :visible="loading || fetching" class="rounded-xl" />

        <MyForm class="space-y-4 mt-6 pb-4" :errors="errors" @submit.prevent="onSubmit">
            <!-- Weekday + Provider -->
            <div class="flex space-x-2">
                <MyWeekDayPicker v-model="selectedWeekday" />

                <MySelect
                    v-model="selectedRemarkProvider"
                    name="provider"
                    :label="t('provider')"
                    :placeholder="t('selectProvider')"
                    :options="providerOptions"
                    group-class="w-full"
                />
            </div>

            <!-- No remarks message -->
            <div
                v-if="noRemarksMessage"
                class="flex flex-col items-center space-y-2 justify-center py-4"
            >
                {{ noRemarksMessage }}
            </div>

            <!-- Remark Suggestions Table -->
            <div v-if="filteredRemarks.length" class="space-y-3">
                <div
                    v-for="remark in filteredRemarks"
                    :key="remark.id"
                    class="flex justify-between items-center px-4 py-3 rounded-xl border shadow-sm transition-colors"
                    :class="{
                        'bg-primary-500 text-white': selectedRemark?.remark === remark.remark,
                        'bg-stone-200 dark:bg-dark-500 text-stone-800 dark:text-stone-100':
                            selectedRemark?.remark !== remark.remark,
                    }"
                >
                    <div class="flex flex-1 items-start justify-between gap-x-10">
                        <div class="flex flex-col items-start">
                            <MyInputLabel class="!pl-0">{{ t('loadingTime') }}</MyInputLabel>
                            <span>{{ differenceFromMinutes(remark.loadingTime) }}</span>
                        </div>

                        <div class="flex flex-col items-start">
                            <MyInputLabel class="!pl-0">{{ t('departureTime') }}</MyInputLabel>
                            <span>{{ differenceFromMinutes(remark.departureTime) }}</span>
                        </div>

                        <div class="flex flex-col items-start flex-1">
                            <MyInputLabel class="!pl-0">{{ t('remark') }}</MyInputLabel>
                            <span>{{ remark.remark || '-' }}</span>
                        </div>
                    </div>

                    <MyButton
                        v-if="selectedRemark?.id === remark.id"
                        type="button"
                        scheme="primary"
                        @click="selectRemark(null)"
                    >
                        {{ t('selected') }}
                    </MyButton>

                    <MyButton v-else type="button" @click="selectRemark(remark)">
                        {{ t('select') }}
                    </MyButton>
                </div>
            </div>

            <!-- Remark Input -->
            <MyTextarea
                v-if="selectedRemark"
                id="remark"
                v-model="data.remark"
                :label="t('remark')"
                name="remark"
                rows="5"
            />

            <!-- Trip, Country, User -->
            <div v-if="selectedRemark" class="grid grid-cols-3 gap-4">
                <MyInput v-model="data.tripNumber" name="tripNumber" :label="t('tripNumber')" />

                <MySelect
                    v-model="data.country"
                    name="country"
                    :label="t('country')"
                    :options="allCountries"
                />
                <MyInput
                    :value="authStore.user?.name ?? ''"
                    :label="t('user')"
                    name="user"
                    disabled
                />
            </div>

            <!-- Truck, Trailer, Flight -->
            <div v-if="selectedRemark" class="grid grid-cols-3 gap-4">
                <MyInput
                    v-model="data.truckNumber"
                    name="truckNumber"
                    :label="t('truck')"
                    :disabled="disableTruckFields"
                />
                <MyInput
                    v-model="data.trailerNumber"
                    name="trailerNumber"
                    :label="t('trailer')"
                    :disabled="disableTruckFields"
                />
                <MyInput
                    v-model="data.flightNumber"
                    name="flightNumber"
                    :label="t('flight')"
                    :disabled="disableFlightField"
                />
            </div>
            <!-- Origin and Destination Inputs -->
            <div v-if="selectedRemark" class="grid grid-cols-2 gap-4">
                <MySelect
                    v-model="data.originPortId"
                    name="originPortId"
                    group-class="w-full"
                    :label="t('origin')"
                    searchable
                    :options="ports.items.value"
                    :loading="ports.fetching.value"
                    @focus="ports.fetch()"
                />
                <MySelect
                    v-model="data.destinationPortIds"
                    name="destinationPortIds"
                    group-class="w-full"
                    :label="t('destination')"
                    searchable
                    :options="ports.items.value"
                    :loading="ports.fetching.value"
                    multiple
                    @focus="ports.fetch()"
                />
            </div>

            <!-- Submit -->
            <div v-if="selectedRemark" class="flex justify-end">
                <MyButton type="submit" scheme="primary">
                    {{ t(isEditing ? 'update' : 'save') }}
                </MyButton>
            </div>
        </MyForm>
    </MyModal>
</template>
